import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFieldValue } from "../../utils/language";
import { Link } from "react-router-dom";

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

const truncateText = (text = "", maxLength = 120) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

const isEndDatePassed = (endDate) => {
  // If no end_date, treat as closed
  if (!endDate || endDate === "" || endDate === null || endDate === undefined) {
    return true;
  }
  try {
    const end = new Date(endDate);
    const now = new Date();
    
    // Check if date is valid
    if (isNaN(end.getTime())) {
      console.error("Invalid end_date format:", endDate);
      return true; // Treat invalid date as closed
    }
    
    // Set time to start of day for both dates for accurate comparison
    // This ensures that if end date is December 10, it's considered passed only after December 10
    end.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    // Return true if end_date is before today (has passed)
    return end < now;
  } catch (error) {
    console.error("Error parsing end_date:", error);
    return true; // If parsing fails, treat as closed
  }
};

function SaudiaCompetitions() {
  const splideRef = useRef();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();

  const normalizeCompetitionData = (data) => {
    if (!data) return [];
    if (Array.isArray(data?.competititon)) return data.competititon;
    if (Array.isArray(data?.competition)) return data.competition;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchCompetitions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleApiCall(
        "get",
        endpoints.saudiaCompetitionList
      );
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const normalized = normalizeCompetitionData(response?.data?.data);
        setCompetitions(normalized);
      } else {
        console.error("Failed to fetch competitions:", response?.message);
        setCompetitions([]);
      }
    } catch (error) {
      console.error("Error fetching competitions:", error);
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompetitions();
  }, [fetchCompetitions]);

  const slides = useMemo(() => {
    if (loading) {
      return [
        <SplideSlide key="loading" className="w-[320px] md:w-[400px] lg:w-[500px]">
          <div className="flex h-[260px] md:h-[220px] lg:h-[260px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}
          </div>
        </SplideSlide>,
      ];
    }

    if (!competitions.length) {
      return [
        <SplideSlide key="empty" className="w-[320px] md:w-[400px] lg:w-[500px]">
          <div className="flex h-[260px] md:h-[220px] lg:h-[260px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
          </div>
        </SplideSlide>,
      ];
    }

    return competitions.map((item, index) => {
      const title =
        getLocalizedFieldValue(item, language, "title") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      const fullDescription =
        getLocalizedFieldValue(item, language, "description") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      // const description = truncateText(fullDescription, 120);
      const descriptionHtml = fullDescription;
      const videoUrl = buildMediaUrl(item?.video);
      const desktopImage = buildMediaUrl(item?.desktop_image);
      const mobileImage = buildMediaUrl(item?.mobile_image);
      const hasVideo = !!videoUrl;
      const hasImage = !!(desktopImage || mobileImage);

      return (
        <SplideSlide key={item?._id || item?.id || index} className="w-[320px] md:w-[400px] lg:w-[500px]">
          <div className="relative h-[180px] md:h-[220px] lg:h-[260px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
            {hasVideo ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
              />
            ) : hasImage ? (
              <picture className="absolute inset-0 w-full h-full">
                {desktopImage && (
                  <source media="(min-width: 768px)" srcSet={desktopImage} />
                )}
                <img
                  className="absolute inset-0 w-full h-full object-cover"
                  src={mobileImage || desktopImage}
                  alt={title}
                  loading="lazy"
                />
              </picture>
            ) : (
              <div className="absolute inset-0 bg-black/70" />
            )}
          </div>
          {(() => {
            const isClosed = isEndDatePassed(item?.end_date);
            return (
              <div className={`flex items-center gap-x-1.5 bg-black absolute left-5 top-2 text-white px-1 rounded-[4px] text-sm ${isClosed ? 'opacity-75' : ''}`}>
                {isClosed ? (
                  <>
                    <span className="closed-icon"></span> {language === "AR" ? "مغلق" : "Closed"}
                  </>
                ) : (
                  <>
                    <span className="live-icon"></span> Live
                  </>
                )}
              </div>
            );
          })()}
          <div className="caption md:px-3 mt-2 lg:mt-0">
            <h3 className="text-white text-lg md:text-2xl font-SaudiaBold mt-4">
              {title}
            </h3>

            {/* <p className={`text-white md:mt-2 lg:mt-1 text-xs md:text-base lg:text-lg font-SaudiaLight md:leading-snug ${language === "AR" ? "pl-4 md:pl-8 lg:pl-20 mt-2 leading-6" : "pr-4 md:pr-8 lg:pr-20"}`}>
              {description}
            </p> */}
            <div
              className={`text-white md:mt-2 lg:mt-1 text-xs md:text-base lg:text-lg font-SaudiaLight md:leading-snug 
                ${language === "AR" 
                  ? "pl-4 md:pl-8 lg:pl-20 mt-2 leading-6" 
                  : "pr-4 md:pr-8 lg:pr-20"
                }`}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />


            {!isEndDatePassed(item?.end_date) && (
              item.link_checkbox ? (
                <a
                  href={language === "AR" 
                    ? item.link_ar
                    : item.link_en}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center mt-2 md:mt-4 md:px-0 md:py-2 bg-saudiaGreen text-white text-[10px] md:text-sm rounded-sm md:rounded-md w-[76px] md:w-[6.625rem] h-5 md:h-[2.125rem] font-SaudiaLight md:font-SaudiaRegular cursor-pointer"
                >
                  {language === "AR" ? "اشترك الآن" : (UI_CONSTANTS.BUTTON_LABELS.ENTER_NOW || "Enter Now")}
                </a>
              ) : (
              <Link
                to={`/enter-to-win/${item._id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center mt-2 md:mt-4 md:px-0 md:py-2 bg-saudiaGreen text-white text-[10px] md:text-sm rounded-sm md:rounded-md w-[76px] md:w-[6.625rem] h-5 md:h-[2.125rem] font-SaudiaLight md:font-SaudiaRegular cursor-pointer"
              >
                {language === "AR"
                  ? "اشترك الآن"
                  : (UI_CONSTANTS.BUTTON_LABELS.ENTER_NOW || "Enter Now")}
              </Link>
              )
            )}
          </div>
        </SplideSlide>
      );
    });
  }, [competitions, loading, language]);

  //console.log("language", language);
  

  return (
    <section id="saudia-competition" className='overflow-hidden bg-white [&_.splide\_\_track]:!overflow-visible'>
      <div className=' bg-black py-12 [clip-path:polygon(10%_0,_100%_0,_100%_100%,_0_100%,_0%_10%)] md:[clip-path:polygon(10%_0,_100%_0,_100%_100%,_0_100%,_0%_20%)] lg:[clip-path:polygon(10%_0,_100%_0,_100%_100%,_0_100%,_0%_38%)]'>
        <div className='px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto'>
          <div className='md:ml-12 lg:ml-28 md:mb-10 gap-x-16'>
			<div className={`flex items-center justify-between mb-8 ${language === "AR" ? "pl-6 md:pl-0" : "pr-6 md:pr-0"}`}>
            <h2 className={`text-white md:text-[2rem] lg:text-[2.625rem] ${language === "AR" ? "text-[22px] font-SaudiaBold":"text-[28px] font-SaudiaBoldItalic"}`}>
              {/* Saudia Competitions */}
              {/* {language === "AR" ? "مسابقات" : "Competitions"} */}
              {language === "AR" ? "المسابقات" : "Competitions"}
            </h2>
			{/* <div className="gap-2 hidden md:flex">
                <button
                  onClick={() => splideRef.current?.go('<')}
                  className="w-8 h-8 md:w-10 md:h-10 border border-white/30 rounded flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 13L1 7L7 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  onClick={() => splideRef.current?.go('>')}
                  className="w-8 h-8 md:w-10 md:h-10 bg-[#006C35] rounded flex items-center justify-center text-white hover:bg-[#005c2d] transition-colors"
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 13L7 7L1 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div> */}
			 </div>
            <div className='skewSlider2
              [&_.splide\_\_pagination]:!mt-12 
              [&_.splide\_\_arrows]:absolute
              [&_.splide\_\_arrows]:-top-[84px]
              [&_.splide\_\_arrows]:right-0
              [&_.splide\_\_arrows]:hidden
              md:[&_.splide\_\_arrows]:flex
              [&_.splide\_\_arrows]:gap-x-4
              [&_.splide\_\_arrows_button]:transform-none
              [&_.splide\_\_arrows_button]:border
              [&_.splide\_\_arrows_button]:border-solid
              [&_.splide\_\_arrows_button]:border-white/40
              [&_.splide\_\_arrows_button]:bg-transparent
              [&_.splide\_\_arrows_button]:w-10
              [&_.splide\_\_arrows_button]:h-10
              [&_.splide\_\_arrows_button_svg]:fill-white
              [&_.splide\_\_arrows_button]:opacity-100
              [&_.splide\_\_arrows_button]:rounded-sm
              [&_.splide\_\_arrows_button]:relative
              [&_.splide\_\_arrows_button]:left-0
              [&_.splide\_\_arrows_button]:right-0
              [&_.splide\_\_arrows_button:not(:disabled)]:bg-saudiaGreen
              [&_.splide\_\_arrows_button:not(:disabled)]:border-saudiaGreen'
            >
              <Splide
				        ref={splideRef}
                key={language}
                options={{
                  rewind: false,
                  gap: "45px",
                  perPage: 2,
                  autoWidth: true,
                  focus: 0,
                  start: 0,
                  perMove: 1,
                  arrows: true,
                  direction: language === "AR" ? "rtl" : "ltr",
                  breakpoints: {
                    767: {
                      gap: "25px",
                    },
                  },
                }}
                aria-label="Saudia Competitions"
              >
                {slides}
              </Splide>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SaudiaCompetitions;

